import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { API_ENDPOINTS } from "../../config/api";
import { formatDate } from "../../utils/dateFormatting";
import { getBotIcon } from "../../utils/botFormatting";

import Button from "../../components/Button";
import { Table, Row, Column, ActionsColumn } from "../../components/Table";
import { LoadingState, EmptyState } from "../../components/CommonStates";
import { Header } from "../../components/AdminTab";
import Modal, {
  ModalTitle,
  ModalDescription,
  ModalActions,
  BotList,
  BotItem,
  BotCheckbox,
  BotLabel,
  BotIcon
} from "../../components/Modal";
import { Form, FormGroup, Label } from "../../components/Form";
import { TextArea } from "../../components/Textarea";
import { Dropdown } from "../../components/Dropdown";

const ResultsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 2rem;
`;

const ResultItem = styled.div`
  background: white;
  border-radius: 0.5rem;
  padding: 0.75rem;
  gap: 0.25rem;
  border: 2px solid #e1e5e9;
  height: 20.5rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ResultHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ResultActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  align-items: flex-end;
`;

const ResultDeleteButton = styled.div`
  display: inline-block;
  color: #6c757d;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: bold;

  &:hover:not(:disabled) {
    color: #dc3545;
  }

  &:disabled {
    color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ResultStatus = styled.span`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-weight: 600;
  color: #ffffff;
  font-size: 0.75rem;

  ${({ $passed }) =>
    $passed
      ? `
    background:rgb(10, 184, 10);
  `
      : `
    background:rgb(218, 25, 41);
  `}
`;

const ResultResponse = styled.div`
  word-break: break-word;
  background: #e5e5e5;
  padding: 1rem;
  border-radius: 0.5rem;
  margin-top: 0.5rem;
  flex-grow: 1;
  overflow-y: auto;
`;

const ResultMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #6c757d;
  margin-top: 0.5rem;
  flex-wrap: wrap;
  line-height: 1.25;
`;

const HistoryItems = styled.div`
  display: flex;
  flex-direction: column;
`;

const HistoryItem = styled.div`
  border-bottom: 0.125rem solid #e1e5e9;
  padding: 2rem 0;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const HistoryHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const HistoryAction = styled.span`
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;

  ${({ $action }) =>
    $action === "run" &&
    `
    background: #007bff;
  `}

  ${({ $action }) =>
    $action === "created" &&
    `
    background:rgb(136, 136, 136);
  `}

  ${({ $action }) =>
    $action === "updated" &&
    `
    background:rgb(239, 132, 0);
  `}
`;

const HistoryMeta = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  color: #6c757d;
`;

const HistoryPromptTest = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LeftActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const RightActions = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const CsvUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CsvUploadError = styled.div`
  color: #dc3545;
  font-size: 0.75rem;
  font-weight: 500;
  max-width: 200px;
  word-wrap: break-word;
`;

function EvalsTab({ user, onError, onSuccess }) {
  const [evals, setEvals] = useState([]);
  const [bots, setBots] = useState([]);
  const [evalResults, setEvalResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingEval, setCreatingEval] = useState(false);
  const [editingEval, setEditingEval] = useState(null);
  const [updatingEval, setUpdatingEval] = useState(false);
  const [deletingEvals, setDeletingEvals] = useState(new Set());
  const [runningEvals, setRunningEvals] = useState(new Set());
  const [viewingResults, setViewingResults] = useState(null);
  const [deletingResults, setDeletingResults] = useState(new Set());
  const [evalHistory, setEvalHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [csvUploadError, setCsvUploadError] = useState("");
  const [evalDeleteConfirmation, setEvalDeleteConfirmation] = useState(null);
  const [resultDeleteConfirmation, setResultDeleteConfirmation] =
    useState(null);
  const [runningAllEvals, setRunningAllEvals] = useState(false);

  const [formData, setFormData] = useState({
    botIds: [],
    prompt: "",
    testType: "equals",
    testString: ""
  });

  const fetchEvals = useCallback(async () => {
    setLoading(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.evals, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setEvals(data.evals || []);
      } else {
        onError(data.error || "Failed to fetch evals");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  const fetchBots = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.adminBots, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();
      if (response.ok) {
        setBots(data.bots || []);
      }
    } catch (error) {
      // Silently fail for bots - the form will still work without them
    }
  }, []);

  const fetchEvalResults = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.evalResults, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();
      if (response.ok) {
        setEvalResults(data.results || []);
      }
    } catch (error) {
      // Silently fail for eval results
    }
  }, []);

  const fetchEvalHistory = useCallback(async (evalId) => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.evalHistory(evalId), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();
      if (response.ok) {
        setEvalHistory(data.history || []);
      }
    } catch (error) {
      // Silently fail for eval history
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const handleCreateEval = () => {
    setFormData({
      botIds: [],
      prompt: "",
      testType: "equals",
      testString: ""
    });
    setCreatingEval(true);
    onError("");
  };

  const handleEditEval = (evalItem) => {
    setFormData({
      botIds: evalItem.botIds || [],
      prompt: evalItem.prompt || "",
      testType: evalItem.testType || "equals",
      testString: evalItem.testString || ""
    });
    setEditingEval(evalItem);
    onError("");
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBotToggle = (botId) => {
    setFormData((prev) => ({
      ...prev,
      botIds: prev.botIds.includes(botId)
        ? prev.botIds.filter((id) => id !== botId)
        : [...prev.botIds, botId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUpdatingEval(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      const url = editingEval
        ? API_ENDPOINTS.updateEval(editingEval.id)
        : API_ENDPOINTS.evals;
      const method = editingEval ? "PUT" : "POST";

      // Prepare request body - add created_by for new evals only, changedBy for updates
      const requestBody = editingEval
        ? { ...formData, changedBy: user?.username || "Unknown" }
        : { ...formData, createdBy: user?.username || "Unknown" };

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess(
          editingEval
            ? "Eval updated successfully!"
            : "Eval created successfully!"
        );
        setCreatingEval(false);
        setEditingEval(null);
        setFormData({
          botIds: [],
          prompt: "",
          testType: "equals",
          testString: ""
        });
        fetchEvals();
        fetchEvalResults();
      } else {
        onError(data.error || "Failed to save eval");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setUpdatingEval(false);
    }
  };

  const handleCancel = () => {
    setCreatingEval(false);
    setEditingEval(null);
    setFormData({
      botIds: [],
      prompt: "",
      testType: "equals",
      testString: ""
    });
    onError("");
  };

  const handleDeleteClick = (evalItem) => {
    setEvalDeleteConfirmation(evalItem);
  };

  const handleDeleteEvalConfirm = async () => {
    if (!evalDeleteConfirmation) return;

    const evalId = evalDeleteConfirmation.id;
    setDeletingEvals((prev) => new Set(prev).add(evalId));
    onError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.deleteEval(evalId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          deletedBy: user?.username || "Unknown"
        })
      });

      const data = await response.json();

      if (response.ok) {
        setEvals((prevEvals) =>
          prevEvals.filter((evaluation) => evaluation.id !== evalId)
        );
        fetchEvalResults(); // Refresh results since the eval was deleted
        onSuccess("Eval deleted successfully");
      } else {
        onError(data.error || "Failed to delete eval");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setDeletingEvals((prev) => {
        const newSet = new Set(prev);
        newSet.delete(evalId);
        return newSet;
      });
      setEvalDeleteConfirmation(null);
    }
  };

  const handleDeleteEvalCancel = () => {
    setEvalDeleteConfirmation(null);
  };

  const handleRunEval = async (evalItem) => {
    const evalId = evalItem.id;
    setRunningEvals((prev) => new Set(prev).add(evalId));
    onError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.runEval(evalId), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          runBy: user?.username || "Unknown"
        })
      });

      const data = await response.json();

      if (response.ok) {
        onSuccess("Eval started successfully!");
        // Refresh eval results after running
        fetchEvalResults();
      } else {
        onError(data.error || "Failed to run eval");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setRunningEvals((prev) => {
        const newSet = new Set(prev);
        newSet.delete(evalId);
        return newSet;
      });
    }
  };

  const getResultsForEval = (evalId) => {
    return evalResults.filter((result) => result.evalId === evalId);
  };

  const handleViewResults = (evalItem) => {
    setViewingResults(evalItem);
    fetchEvalHistory(evalItem.id);
  };

  const handleCloseResults = () => {
    setViewingResults(null);
    setEvalHistory([]);
  };

  const handleDeleteResult = (result) => {
    setResultDeleteConfirmation(result);
  };

  const handleDeleteResultConfirm = async () => {
    if (!resultDeleteConfirmation) return;

    const resultId = resultDeleteConfirmation.id;
    setDeletingResults((prev) => new Set(prev).add(resultId));
    onError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.deleteEvalResult(resultId), {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      const data = await response.json();

      if (response.ok) {
        setEvalResults((prevResults) =>
          prevResults.filter((r) => r.id !== resultId)
        );
        onSuccess("Eval result deleted successfully");
      } else {
        onError(data.error || "Failed to delete eval result");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setDeletingResults((prev) => {
        const newSet = new Set(prev);
        newSet.delete(resultId);
        return newSet;
      });
      setResultDeleteConfirmation(null);
    }
  };

  const handleDeleteResultCancel = () => {
    setResultDeleteConfirmation(null);
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith(".csv")) {
      onError("Please select a valid CSV file");
      return;
    }

    setUploadingCsv(true);
    setCsvUploadError("");
    onError("");

    try {
      const formData = new FormData();
      formData.append("csv", file);
      formData.append("uploadedBy", user?.username || "Unknown");

      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.uploadEvalCsv, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        // Handle the detailed response format
        const { successCount, errorCount } = data;

        let message = `CSV processing completed: ${successCount} evals created`;
        if (errorCount > 0) {
          message += `, ${errorCount} errors`;
        }

        onSuccess(message);
        fetchEvals(); // Refresh the evals list
      } else {
        const errorMessage = data.error || "Failed to upload CSV";
        setCsvUploadError(errorMessage);
        onError(errorMessage);
      }
    } catch (error) {
      const errorMessage = "Network error. Please try again.";
      setCsvUploadError(errorMessage);
      onError(errorMessage);
    } finally {
      setUploadingCsv(false);
      // Reset the file input
      event.target.value = "";
    }
  };

  const handleCsvButtonClick = () => {
    document.getElementById("csv-upload-input").click();
  };

  const handleRunAllEvals = async () => {
    setRunningAllEvals(true);
    onError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.runAllEvals, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          runBy: user?.username || "Unknown"
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Display detailed summary if available
        if (data.summary) {
          const { summary } = data;
          const message = `Run completed: ${summary.successfulRuns}/${summary.totalEvals} evals successful, ${summary.totalPassed}/${summary.totalBotsTested} tests passed`;
          onSuccess(message);
        } else {
          onSuccess("All evals started successfully!");
        }

        // Refresh eval results after running
        fetchEvalResults();
      } else {
        onError(data.error || "Failed to run all evals");
      }
    } catch (error) {
      onError("Network error. Please try again.");
    } finally {
      setRunningAllEvals(false);
    }
  };

  const getBotById = (botId) => {
    return bots.find((bot) => bot.id === botId);
  };

  useEffect(() => {
    fetchEvals();
    fetchBots();
    fetchEvalResults();
  }, [fetchEvals, fetchBots, fetchEvalResults]);

  if (loading && !evals.length) {
    return <LoadingState>Loading evals...</LoadingState>;
  }

  return (
    <>
      <Header>
        <HeaderActions>
          <LeftActions>
            <CsvUploadContainer>
              <Button
                onClick={handleCsvButtonClick}
                disabled={uploadingCsv}
                variant="secondary"
              >
                {uploadingCsv ? "Uploading..." : "Upload CSV"}
              </Button>
              {csvUploadError && (
                <CsvUploadError>{csvUploadError}</CsvUploadError>
              )}
              <HiddenFileInput
                id="csv-upload-input"
                type="file"
                accept=".csv"
                onChange={handleCsvUpload}
              />
            </CsvUploadContainer>
          </LeftActions>
          <RightActions>
            <Button
              onClick={handleRunAllEvals}
              disabled={runningAllEvals || evals.length === 0}
              variant="secondary"
            >
              {runningAllEvals ? "Running all evals..." : "Run all evals"}
            </Button>
            <Button onClick={handleCreateEval}>Create new eval</Button>
          </RightActions>
        </HeaderActions>
      </Header>

      {evals.length === 0 ? (
        <EmptyState>
          <h4>No evals found</h4>
          <p>Create your first eval to get started.</p>
        </EmptyState>
      ) : (
        <Table columns="2.5fr 1fr 1fr 2fr">
          <Row>
            <Column>Prompt</Column>
            <Column>Expected result</Column>
            <Column>Created</Column>
            <ActionsColumn>Actions</ActionsColumn>
          </Row>

          {evals.map((evalItem) => {
            const evalResults = getResultsForEval(evalItem.id);

            return (
              <Row key={evalItem.id} style={{ minHeight: "5rem" }}>
                <Column data-label="Prompt">{evalItem.prompt}</Column>
                <Column data-label="Expected result">
                  {evalItem.testType.charAt(0).toUpperCase() +
                    evalItem.testType.slice(1)}{" "}
                  {evalItem.testString}
                </Column>
                <Column data-label="Created">
                  {formatDate(evalItem.createdAt)}
                </Column>
                <ActionsColumn data-label="Actions">
                  {runningEvals.has(evalItem.id) ? (
                    "Running eval for " + evalItem.botIds.length + " bots..."
                  ) : (
                    <>
                      <Button
                        onClick={() => handleRunEval(evalItem)}
                        disabled={
                          runningEvals.has(evalItem.id) ||
                          deletingEvals.has(evalItem.id)
                        }
                      >
                        Run
                      </Button>
                      {evalResults.length > 0 && (
                        <Button
                          onClick={() => handleViewResults(evalItem)}
                          disabled={
                            deletingEvals.has(evalItem.id) ||
                            runningEvals.has(evalItem.id)
                          }
                        >
                          View
                        </Button>
                      )}
                      <Button
                        onClick={() => handleEditEval(evalItem)}
                        disabled={
                          deletingEvals.has(evalItem.id) ||
                          runningEvals.has(evalItem.id)
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => handleDeleteClick(evalItem)}
                        disabled={
                          deletingEvals.has(evalItem.id) ||
                          runningEvals.has(evalItem.id)
                        }
                      >
                        {deletingEvals.has(evalItem.id)
                          ? "Deleting..."
                          : "Delete"}
                      </Button>
                    </>
                  )}
                </ActionsColumn>
              </Row>
            );
          })}
        </Table>
      )}

      {/* Create/Edit Modal */}
      <Modal isOpen={creatingEval || !!editingEval} onClose={handleCancel}>
        <ModalTitle>{editingEval ? "Edit eval" : "Create eval"}</ModalTitle>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="prompt">Prompt</Label>
            <TextArea
              id="prompt"
              value={formData.prompt}
              onChange={(e) => handleFormChange("prompt", e.target.value)}
              placeholder="Enter the test prompt..."
              disabled={updatingEval}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="testType">Test type</Label>
            <Dropdown
              id="testType"
              value={formData.testType}
              onChange={(e) => handleFormChange("testType", e.target.value)}
              disabled={updatingEval}
              required
            >
              <option value="equals">Equals</option>
              <option value="contains">Contains</option>
            </Dropdown>
          </FormGroup>

          <FormGroup>
            <Label htmlFor="testString">Expected result</Label>
            <TextArea
              id="testString"
              value={formData.testString}
              onChange={(e) => handleFormChange("testString", e.target.value)}
              placeholder="Enter the expected result..."
              disabled={updatingEval}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Bots</Label>
            {bots.length === 0 ? (
              <div
                style={{
                  padding: "1rem",
                  color: "#666",
                  textAlign: "center",
                  border: "0.125rem solid #e1e5e9",
                  borderRadius: "8px"
                }}
              >
                No bots available
              </div>
            ) : (
              <BotList>
                {bots.map((bot) => (
                  <BotItem key={bot.id} onClick={() => handleBotToggle(bot.id)}>
                    <BotCheckbox
                      type="checkbox"
                      id={`bot-${bot.id}`}
                      checked={formData.botIds.includes(bot.id)}
                      onChange={() => {}}
                      disabled={updatingEval}
                    />
                    <BotLabel htmlFor={`bot-${bot.id}`}>
                      <BotIcon>{getBotIcon(bot)}</BotIcon>
                      {bot.name}
                    </BotLabel>
                  </BotItem>
                ))}
              </BotList>
            )}
          </FormGroup>

          <ModalActions>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              disabled={updatingEval}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={updatingEval}>
              {updatingEval
                ? editingEval
                  ? "Updating..."
                  : "Creating..."
                : editingEval
                ? "Update"
                : "Create"}
            </Button>
          </ModalActions>
        </Form>
      </Modal>
      {/* Results Modal */}
      <Modal
        isOpen={!!viewingResults}
        onClose={handleCloseResults}
        size="large"
      >
        <ModalTitle>Eval results</ModalTitle>

        <HistoryItems>
          {loadingHistory ? (
            <LoadingState>Loading...</LoadingState>
          ) : evalHistory.length === 0 ? (
            <EmptyState>No history available</EmptyState>
          ) : (
            evalHistory.map((historyItem) => {
              // Get results for this specific run if it's a run action
              const runResults =
                historyItem.action === "run"
                  ? evalResults.filter(
                      (result) => result.runId === historyItem.runId
                    )
                  : [];

              return (
                <HistoryItem key={historyItem.id}>
                  <HistoryHeader>
                    <HistoryAction $action={historyItem.action}>
                      {historyItem.action}
                    </HistoryAction>
                    <HistoryMeta>
                      {historyItem.changedBy} ·{" "}
                      {formatDate(historyItem.changedAt)}
                    </HistoryMeta>
                  </HistoryHeader>

                  <HistoryPromptTest>
                    "{historyItem.prompt}" expected to{" "}
                    {historyItem.testType === "equals" ? "be" : "contain"}{" "}
                    {historyItem.testString}
                  </HistoryPromptTest>

                  {/* Show run results if this is a run action */}
                  {historyItem.action === "run" && runResults.length === 0 ? (
                    <EmptyState>No results found for this run</EmptyState>
                  ) : (
                    runResults.length > 0 && (
                      <ResultsContainer>
                        {runResults.map((result) => {
                          const bot = getBotById(result.botId);
                          return (
                            <ResultItem key={result.id}>
                              <ResultHeader>
                                <ResultHeaderText>
                                  <strong>
                                    {bot ? bot.name : result.botId}
                                  </strong>
                                </ResultHeaderText>
                                <ResultActions>
                                  <ResultStatus $passed={result.testPassed}>
                                    {result.testPassed ? "PASS" : "FAIL"}
                                  </ResultStatus>
                                </ResultActions>
                              </ResultHeader>
                              <ResultResponse>{result.response}</ResultResponse>
                              <ResultMeta>
                                <span>
                                  {result.botId} · {result.provider} ·{" "}
                                  {result.model} ·{" "}
                                  <ResultDeleteButton
                                    onClick={() => handleDeleteResult(result)}
                                    disabled={deletingResults.has(result.id)}
                                  >
                                    {deletingResults.has(result.id)
                                      ? "Deleting..."
                                      : "Delete"}
                                  </ResultDeleteButton>
                                </span>
                              </ResultMeta>
                            </ResultItem>
                          );
                        })}
                      </ResultsContainer>
                    )
                  )}
                </HistoryItem>
              );
            })
          )}
        </HistoryItems>

        <ModalActions>
          <Button onClick={handleCloseResults}>Close</Button>
        </ModalActions>
      </Modal>

      {/* Eval Delete Confirmation Modal */}
      <Modal isOpen={!!evalDeleteConfirmation} onClose={handleDeleteEvalCancel}>
        <ModalTitle>Delete eval?</ModalTitle>
        <ModalDescription>
          Are you sure you want to permanently delete this eval? This action
          cannot be undone.
        </ModalDescription>
        <ModalActions>
          <Button variant="secondary" onClick={handleDeleteEvalCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteEvalConfirm}
            disabled={deletingEvals.has(evalDeleteConfirmation?.id)}
          >
            {deletingEvals.has(evalDeleteConfirmation?.id)
              ? "Deleting..."
              : "Delete"}
          </Button>
        </ModalActions>
      </Modal>

      {/* Result Delete Confirmation Modal */}
      <Modal
        isOpen={!!resultDeleteConfirmation}
        onClose={handleDeleteResultCancel}
      >
        <ModalTitle>Delete eval result?</ModalTitle>
        <ModalDescription>
          Are you sure you want to permanently delete this eval result? This
          action cannot be undone.
        </ModalDescription>
        <ModalActions>
          <Button variant="secondary" onClick={handleDeleteResultCancel}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteResultConfirm}
            disabled={deletingResults.has(resultDeleteConfirmation?.id)}
          >
            {deletingResults.has(resultDeleteConfirmation?.id)
              ? "Deleting..."
              : "Delete"}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}

export default EvalsTab;
